'use client';

import { OwnedCoursePlans } from './owned-course-plans';
import type { OwnedCourseCredit } from '@/lib/owned-course-plans';
import { isUpcomingStudentBooking, studentBookingDateParts } from '@/lib/student-booking-time';
import { StudentRecords } from './student-records';
import type { GroupClassRecord } from '@/lib/group-class-record';
import { TeacherToday } from './teacher-today';
const TeacherCompensation = dynamic(() => import('./teacher-compensation').then(m => m.TeacherCompensation));
import { TeacherCalendarTimeline } from './teacher-calendar-timeline';
import calendarShell from './teacher-calendar-shell.module.css';
import { MemberRecoveryNotice } from './member-recovery-notice';

import { StudentCancellationButton } from './cancellation-panel';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { formatDurationMinutes } from '@workspace/booking/format-duration';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { MiniAppPageTransition } from './miniapp-page-transition';
import { MiniAppPageSkeleton, CoursePlansSkeleton } from './miniapp-page-skeleton';
import { useMiniAppTabData } from './miniapp-tab-cache';
import { getMiniAppSection } from '@/lib/page-data-policy';
import { LineLoginBootstrap } from './line-login-bootstrap';
import {
  closeMiniAppTeacherAvailabilityAction,
  createMiniAppTeacherAvailabilityAction,
  removeMiniAppBookingParticipantAction,
  updateMiniAppTeacherAvailabilityAction,
} from './actions';
import { BookingInvitationButton } from './booking-invitation-button';
import { AppOverlayPortal } from './app-overlay-portal';
import type { MiniAppCoursePlan } from './course-plan-browser';
import { DevPreviewToolbar, type DevPreviewMember } from './dev-preview-toolbar';
import { getReadableBrandText } from '@/lib/theme';
import { MemberQrCard } from './member-qr-card';
import memberCardStyles from './home-member-card.module.css';
const TeacherCourseSale = dynamic(() => import('./teacher-course-sale').then((module) => module.TeacherCourseSale), { loading: () => <MiniAppPageSkeleton path="/teacher/sales" /> });
import type { MiniAppBookingSlot } from './student-booking-browser';
const TeacherBooking = dynamic(() => import('./teacher-booking').then((module) => module.TeacherBooking));
const StudentBookingBrowser = dynamic(() => import('./student-booking-browser').then((module) => module.StudentBookingBrowser), { loading: () => <MiniAppPageSkeleton path="/booking" /> });
const CoursePlanBrowser = dynamic(() => import('./course-plan-browser').then((module) => module.CoursePlanBrowser), { loading: CoursePlansSkeleton });
import { BookedTeacherStrip, TeacherBrowser, type MiniAppTeacher } from './teacher-browser';
import type { GroupClassSummary } from './group-class-browser';

type StudentTab = 'home' | 'booking' | 'courses' | 'teachers' | 'records';
type TeacherTab = 'today' | 'calendar' | 'sales' | 'history' | 'compensation';
type Credit = OwnedCourseCredit & { venueId: string | null; upgradeRules?: { ruleId: string; targetCourseId: string; amount: number }[] };
type Slot = MiniAppBookingSlot;
type Participant = { userId: string; name: string | null; role: string; status: string; attendanceStatus: string };
type Booking = { groupInfo?: GroupClassRecord | null; planName?: string | null; creditCourseName?: string | null; guestCount?: number; attendedGuestCount?: number; upgradeCharge?: { amount: number; status: string } | null; reviewStatus?: string | null; cancellationReason?: string | null; groupStatus?: string | null; groupSessionId?: string | null; id: string; courseName: string; startsAtLabel: string; status: string; teacherName: string; venueName: string; roomName: string | null; resourceNames: string[]; isPrimary?: boolean; startsAt?: string; endsAt?: string; participants: Participant[] };
type AvailabilityLocation = { venueId: string; roomId: string };
type AvailabilityCloseScope = 'SINGLE' | 'FOLLOWING' | 'SERIES';
type TeacherOccurrence = { id: string; seriesId: string | null; startsAt: string; endsAt: string; venueId: string; roomId: string | null; locations: AvailabilityLocation[]; status: string; minBookingNoticeMinutes: number; courseIds: string[] };
type TeacherHistory = { id: string; courseName: string; startsAtLabel: string; durationMinutes: number; participantCount: number; attendedCount: number; outcome: string };
type SimpleOption = { id: string; name: string; iconUrl?: string | null };
type RoomOption = SimpleOption & { venueId: string };
type TeacherSale = { refundedAmount?: number; id: string; memberName: string; planId: string | null; planName: string; venueName: string; status: string; totalAmount: number; paidAmount: number; note: string | null; soldAt: string; payments: Array<{ id: string; amount: number; paymentMethod: string | null; note: string | null; paidAt: string }> };
type MiniAppNavigateOptions = { replace?: boolean; overlay?: boolean; scrollToTop?: boolean; state?: Record<string, unknown> };
type SaleDialog = 'plan' | 'member';
type SaleView = { mode: 'complete' | 'detail'; saleId: string } | null;
type CalendarRoute = { mode: 'list' | 'new' | 'detail' | 'edit'; occurrenceId: string | null };

export type MiniAppPageProps = {
  timezone?: string;
  storeId: string;
  dataRevision: string;
  storeName: string;
  storeIconUrl: string | null;
  brandPrimaryColor: string;
  homeEyebrow: string | null;
  homeTitle: string | null;
  homeDescription: string | null;
  canClaimLegacyMember: boolean;
  userName: string | null;
  currentUserId: string | null;
  recoveryNoticeExpiresAt?: string | null;
  pendingMember?: boolean;
  memberQrToken: string | null;
  memberCode: string | null;
  liffId?: string | null;
  devPreviewEnabled: boolean;
  previewMembers: DevPreviewMember[];
  plans: MiniAppCoursePlan[];
  credits: Credit[];
  slots: Slot[];
  groupSessions: GroupClassSummary[];
  bookingCourses: SimpleOption[];
  bookingVenues: SimpleOption[];
  bookingDates: string[];
  bookings: Booking[];
  teacherBookings: Booking[];
  teacherHistory: TeacherHistory[];
  teacherOccurrences: TeacherOccurrence[];
  teacherVenues: SimpleOption[];
  teacherRooms: RoomOption[];
  teacherCourses: SimpleOption[];
  teacherSales: TeacherSale[];
  teachers: MiniAppTeacher[];
  bookedTeacherIds: string[];
  isTeacher: boolean;
  initialPath: string;
};

const studentPaths: Record<StudentTab, string> = { home: '/home', booking: '/booking', courses: '/courses', teachers: '/teachers', records: '/records' };
const teacherPaths: Record<TeacherTab, string> = { today: '/teacher/today', calendar: '/teacher/calendar', sales: '/teacher/sales', history: '/teacher/records', compensation: '/teacher/compensation' };

function normalizeAppPath(path: string) {
  const normalized = `/${path}`.replace(/\/{2,}/g, '/').replace(/\/$/, '');
  return normalized || '/home';
}

function resolveCalendarRoute(path: string): CalendarRoute {
  if (path === '/teacher/calendar/new') return { mode: 'new', occurrenceId: null };
  const editMatch = path.match(/^\/teacher\/calendar\/([^/]+)\/edit$/);
  if (editMatch) return { mode: 'edit', occurrenceId: editMatch[1]! };
  const detailMatch = path.match(/^\/teacher\/calendar\/([^/]+)$/);
  if (detailMatch) return { mode: 'detail', occurrenceId: detailMatch[1]! };
  return { mode: 'list', occurrenceId: null };
}

function resolveRoute(path: string, isTeacher: boolean) {
  const normalized = normalizeAppPath(path);
  if (isTeacher && (normalized === '/teacher' || normalized.startsWith('/teacher/'))) {
    const teacherTab: TeacherTab = normalized.startsWith('/teacher/compensation') ? 'compensation' : normalized.startsWith('/teacher/calendar')
      ? 'calendar'
      : normalized.startsWith('/teacher/sales')
        ? 'sales'
        : normalized.startsWith('/teacher/records')
          ? 'history'
          : 'today';
    const saleDialog: SaleDialog | null = normalized === '/teacher/sales/new/plan' || normalized === '/teacher/sales/plan'
      ? 'plan'
      : normalized === '/teacher/sales/new/member' || normalized === '/teacher/sales/member'
        ? 'member'
        : null;
    const isSaleCreating = normalized === '/teacher/sales/new' || normalized.startsWith('/teacher/sales/new/') || Boolean(saleDialog);
    const completedSaleMatch = normalized.match(/^\/teacher\/sales\/complete\/([^/]+)$/);
    const saleDetailMatch = normalized.match(/^\/teacher\/sales\/([^/]+)$/);
    const saleView: SaleView = completedSaleMatch
      ? { mode: 'complete', saleId: completedSaleMatch[1]! }
      : saleDetailMatch && !saleDialog && !isSaleCreating
        ? { mode: 'detail', saleId: saleDetailMatch[1]! }
        : null;
    const calendarRoute = teacherTab === 'calendar'
      ? resolveCalendarRoute(normalized)
      : { mode: 'list' as const, occurrenceId: null };
    return { viewerMode: 'teacher' as const, studentTab: 'home' as StudentTab, teacherTab, saleDialog, saleView, isSaleCreating, calendarRoute };
  }
  const studentTab: StudentTab = normalized.startsWith('/booking')
    ? 'booking'
    : normalized.startsWith('/courses')
      ? 'courses'
      : normalized.startsWith('/teachers')
        ? 'teachers'
      : normalized.startsWith('/records')
        ? 'records'
        : 'home';
  return { viewerMode: 'student' as const, studentTab, teacherTab: 'today' as TeacherTab, saleDialog: null, saleView: null as SaleView, isSaleCreating: false, calendarRoute: { mode: 'list' as const, occurrenceId: null } };
}

const statusLabels: Record<string, string> = {
  BOOKED: '已預約', CANCELLED: '已取消', COMPLETED: '已完成', CHECKED_IN: '已核銷', NO_SHOW: '缺席',
  ACTIVE: '使用中', EXPIRED: '已到期', FROZEN: '已凍結', OPEN: '可預約', CLOSED: '已關閉', TAUGHT: '已授課',
};

function statusLabel(status: string) {
  return statusLabels[status] ?? status;
}

function addDays(value: Date, days: number) {
  const result = new Date(value);
  result.setDate(result.getDate() + days);
  return result;
}

function startOfWeek(value: Date) {
  const result = new Date(value);
  const weekday = result.getDay();
  result.setDate(result.getDate() - (weekday === 0 ? 6 : weekday - 1));
  result.setHours(0, 0, 0, 0);
  return result;
}

function localDateTime(value: string) {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

type IconName = 'home' | 'calendar' | 'timetable' | 'qr' | 'number' | 'course' | 'history' | 'clock' | 'location' | 'user' | 'teacher' | 'arrow';
const iconPaths: Record<IconName, ReactNode> = {
  home: <><path d="M3 10.8 12 3l9 7.8"/><path d="M5.5 9.5V21h13V9.5"/><path d="M9.5 21v-7h5v7"/></>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M8 3v4M16 3v4M3 10h18"/></>,
  number: <><path d="M10 3 8 21M16 3l-2 18M4 9h16M3 15h16"/></>,
  qr: <><rect x="3" y="3" width="6" height="6" rx="1"/><rect x="15" y="3" width="6" height="6" rx="1"/><rect x="3" y="15" width="6" height="6" rx="1"/><path d="M15 15h3v3h3M15 21h3M21 15v-3M12 3v3M3 12h3"/></>,
  course: <><circle cx="12" cy="12" r="9"/><path d="M12 7v10M8.5 9 12 7l3.5 2"/></>,
  timetable: <><rect x="3" y="5" width="18" height="16" rx="3"/><path d="M8 3v4M16 3v4M3 10h18M7 14h2M12 14h2M7 17h2M12 17h2"/></>,
  history: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5M12 7v5l3 2"/></>,
  clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  location: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></>,
  user: <><circle cx="12" cy="8" r="4"/><path d="M4.5 21a7.5 7.5 0 0 1 15 0"/></>,
  teacher: <><path d="M4 5h16v11H4zM8 21l4-5 4 5"/><path d="M8 9h8M8 12h5"/></>,
  arrow: <path d="m9 18 6-6-6-6"/>,
};

function Icon({ name }: { name: IconName }) {
  return <svg className="app-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{iconPaths[name]}</svg>;
}

function SectionTitle({ eyebrow, children, action }: { eyebrow?: string; children: ReactNode; action?: ReactNode }) {
  return <div className="section-title"><div>{eyebrow && <span>{eyebrow}</span>}<h2>{children}</h2></div>{action}</div>;
}

function EmptyState({ title, text, action }: { title: string; text: string; action?: ReactNode }) {
  return <div className="empty-state"><div className="empty-orbit"><span /></div><h3>{title}</h3><p>{text}</p>{action}</div>;
}

function StatusPill({ status }: { status: string }) {
  return <span className={`status-pill status-${status.toLowerCase()}`}>{statusLabel(status)}</span>;
}

function AppButton({ children, className = '', ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} className={`app-button ${className}`}>{children}</button>;
}

function createStoreMonogram(storeName: string) {
  const words = storeName.trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  return Array.from(words[0] || '課').slice(0, 2).join('').toUpperCase();
}

export function MiniAppHomeClient(props: MiniAppPageProps) {
  const [routePath, setRoutePath] = useState(() => normalizeAppPath(props.initialPath));
  const { data, hasData, ready, firstReveal, animateReturn, beginVisit, hasCachedPage } = useMiniAppTabData(props, routePath);
  const { storeName, storeIconUrl, brandPrimaryColor, homeEyebrow, homeTitle, homeDescription, userName, canClaimLegacyMember, currentUserId, memberQrToken, memberCode, liffId, devPreviewEnabled, previewMembers, plans, credits, slots, groupSessions, bookingCourses, bookingVenues, bookingDates, bookings, teacherBookings, teacherHistory, teacherOccurrences, teacherVenues, teacherRooms, teacherCourses, teacherSales, teachers, bookedTeacherIds, isTeacher } = data;
  const initialPath = props.initialPath;
  const router = useRouter();
  const [isNavigating, startNavigation] = useTransition();
  const [profileOpen, setProfileOpen] = useState(false);
  const overlayReturnPathRef = useRef('/home');
  const [calendarView, setCalendarView] = useState<'week' | 'month'>('week');
  const [calendarAnchor, setCalendarAnchor] = useState(() => new Date());
  const { viewerMode, studentTab, teacherTab, saleDialog, saleView, isSaleCreating, calendarRoute } = resolveRoute(routePath, isTeacher);
  const selectedTeacherSlug = routePath.match(/^\/teachers\/([^/]+)$/)?.[1] ?? null;
  const scopedBookingTeacherSlug = routePath.match(/^\/booking\/teacher\/([^/]+)$/)?.[1] ?? null;
  const scopedBookingTeacher = scopedBookingTeacherSlug ? teachers.find((item) => item.slug === scopedBookingTeacherSlug) ?? null : null;
  const bookedTeachers = bookedTeacherIds.flatMap((teacherId) => {
    const matched = teachers.find((item) => item.id === teacherId);
    return matched ? [matched] : [];
  });
  const memberQrOpen = routePath === '/member' || routePath === '/teacher/member';
  const viewTransitionKey = routePath.startsWith('/teacher/sales/new') ? '/teacher/sales/new' : routePath;

  const isMemberOverlay = routePath === '/member' || routePath === '/teacher/member';
  const routeDataReady = isMemberOverlay || ready;

  useEffect(() => {
    setRoutePath(normalizeAppPath(window.location.pathname));
  }, [initialPath]);

  const navigateTo = useCallback((path: string, options?: MiniAppNavigateOptions) => {
    const normalized = normalizeAppPath(path);
    const isMember = normalized === '/member' || normalized === '/teacher/member';
    if (!isMember) beginVisit(normalized);
    const targetSection = getMiniAppSection(normalized, isTeacher);
    const needsFreshData = targetSection === 'records' || targetSection === 'courses';
    if (!isMember && (isNavigating || ((needsFreshData || !hasCachedPage(normalized)) && targetSection !== getMiniAppSection(initialPath, isTeacher)))) {
      setRoutePath(normalized);
      setProfileOpen(false);
      startNavigation(() => {
        if (options?.replace) router.replace(normalized, { scroll: options?.scrollToTop ?? true });
        else router.push(normalized, { scroll: options?.scrollToTop ?? true });
      });
      return;
    }
    const state = { ...options?.state, miniapp: true, overlay: Boolean(options?.overlay) };
    if (options?.replace) window.history.replaceState(state, '', normalized);
    else window.history.pushState(state, '', normalized);
    if (options?.scrollToTop) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setProfileOpen(false);
    setRoutePath(normalized);
  }, [initialPath, isTeacher, isNavigating, router, beginVisit, hasCachedPage]);

  const closeOverlay = useCallback((fallbackPath: string) => {
    if (window.history.state?.miniapp && window.history.state?.overlay) window.history.back();
    else navigateTo(fallbackPath, { replace: true });
  }, [navigateTo]);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    const syncRoute = (event: PopStateEvent) => {
      const path = normalizeAppPath(window.location.pathname);
      beginVisit(path);
      setRoutePath(path);
      if (typeof event.state?.scrollY === 'number') {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => window.scrollTo({ top: event.state.scrollY, left: 0, behavior: 'auto' }));
        });
      }
    };
    window.addEventListener('popstate', syncRoute);
    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
      window.removeEventListener('popstate', syncRoute);
    };
  }, [beginVisit]);

  useEffect(() => {
    if (!isTeacher && (routePath === '/teacher' || routePath.startsWith('/teacher/'))) navigateTo('/home', { replace: true });
  }, [isTeacher, navigateTo, routePath]);

  const openMemberQr = useCallback(() => {
    overlayReturnPathRef.current = routePath === '/member' ? '/home' : routePath;
    navigateTo(viewerMode === 'teacher' ? '/teacher/member' : '/member', { overlay: true });
    setProfileOpen(false);
  }, [navigateTo, routePath, viewerMode]);

  const closeMemberQr = useCallback(() => {
    const fallback = routePath === '/teacher/member' && overlayReturnPathRef.current === '/home'
      ? '/teacher/today'
      : overlayReturnPathRef.current || '/home';
    closeOverlay(fallback);
  }, [closeOverlay, routePath]);

  const goStudent = useCallback((tab: StudentTab) => navigateTo(studentPaths[tab]), [navigateTo]);
  const openTeacher = useCallback((selected: MiniAppTeacher) => navigateTo(`/teachers/${selected.slug}`, { scrollToTop: true }), [navigateTo]);
  const closeTeacher = useCallback(() => navigateTo('/teachers', { scrollToTop: true }), [navigateTo]);
  const bookTeacher = useCallback((selected: MiniAppTeacher) => navigateTo(`/booking/teacher/${selected.slug}`, { scrollToTop: true }), [navigateTo]);
  const goTeacher = useCallback((tab: TeacherTab) => navigateTo(teacherPaths[tab]), [navigateTo]);
  const openCoursePlan = useCallback((planId: string) => navigateTo(`/courses/plans/${planId}`, { overlay: true }), [navigateTo]);
  const closeCoursePlan = useCallback(() => closeOverlay('/courses'), [closeOverlay]);
  const openSaleDialog = useCallback((dialog: SaleDialog) => navigateTo(`/teacher/sales/new/${dialog}`, { overlay: true }), [navigateTo]);
  const closeSaleDialog = useCallback(() => closeOverlay('/teacher/sales/new'), [closeOverlay]);
  const createSale = useCallback(() => navigateTo('/teacher/sales/new'), [navigateTo]);
  const openSaleDetail = useCallback((saleId: string) => navigateTo(`/teacher/sales/${saleId}`), [navigateTo]);
  const showSaleComplete = useCallback((saleId: string) => navigateTo(`/teacher/sales/complete/${saleId}`), [navigateTo]);
  const closeSaleView = useCallback(() => navigateTo('/teacher/sales'), [navigateTo]);
  const openAvailabilityCreate = useCallback(() => navigateTo('/teacher/calendar/new'), [navigateTo]);
  const openAvailabilityDetail = useCallback((occurrenceId: string) => navigateTo(`/teacher/calendar/${occurrenceId}`), [navigateTo]);
  const openAvailabilityEdit = useCallback((occurrenceId: string) => navigateTo(`/teacher/calendar/${occurrenceId}/edit`), [navigateTo]);
  const closeAvailabilityView = useCallback(() => navigateTo('/teacher/calendar'), [navigateTo]);
  const createAvailability = useCallback(async (formData: FormData) => {
    await createMiniAppTeacherAvailabilityAction(formData);
    navigateTo('/teacher/calendar');
  }, [navigateTo]);
  const updateAvailability = useCallback(async (formData: FormData) => {
    const occurrenceId = String(formData.get('occurrenceId') ?? '');
    await updateMiniAppTeacherAvailabilityAction(formData);
    navigateTo(occurrenceId ? `/teacher/calendar/${occurrenceId}` : '/teacher/calendar');
  }, [navigateTo]);
  const closeAvailability = useCallback(async (formData: FormData) => {
    await closeMiniAppTeacherAvailabilityAction(formData);
    navigateTo('/teacher/calendar');
  }, [navigateTo]);
  const totalCredits = credits.reduce((sum, credit) => sum + credit.remainingCredits, 0);
  const reservedCredits = credits.reduce((sum, credit) => sum + credit.reservedCredits, 0);
  const [bookingClock, setBookingClock] = useState(() => Date.now());
  useEffect(() => {
    if (!currentUserId || viewerMode !== 'student' || !['records', 'courses'].includes(studentTab)) return;
    const refreshVisiblePage = () => {
      if (document.visibilityState === 'visible') router.refresh();
    };
    const timer = window.setInterval(refreshVisiblePage, 15_000);
    window.addEventListener('focus', refreshVisiblePage);
    document.addEventListener('visibilitychange', refreshVisiblePage);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshVisiblePage);
      document.removeEventListener('visibilitychange', refreshVisiblePage);
    };
  }, [currentUserId, router, studentTab, viewerMode]);
  useEffect(() => {
    const refresh = () => setBookingClock(Date.now());
    refresh();
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, []);
  const upcomingBookings = bookings.filter((booking) => isUpcomingStudentBooking(booking, bookingClock));
  const nextBooking = [...upcomingBookings].sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? ''))[0];
  const nextBookingDate = nextBooking?.startsAt ? studentBookingDateParts(nextBooking.startsAt, props.timezone ?? 'Asia/Taipei') : null;
  const resolvedHomeTitle = homeTitle
    ? homeTitle
    : userName
      ? <>{userName}，<span className="hero-welcome">歡迎回來</span></>
      : '管理你的課程與預約';

  const resolvedHomeDescription = homeDescription || (nextBooking
    ? `下一堂 ${nextBooking.courseName} 已為你保留。`
    : '從這裡查看課程、額度，或前往預約選擇適合的時段。');
  const calendarDays = useMemo(() => {
    const first = calendarView === 'week' ? startOfWeek(calendarAnchor) : startOfWeek(new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth(), 1));
    const monthEnd = new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() + 1, 0);
    const leadingDays = (new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth(), 1).getDay() + 6) % 7;
    const count = calendarView === 'week' ? 7 : Math.ceil((leadingDays + monthEnd.getDate()) / 7) * 7;
    return Array.from({ length: count }, (_, index) => addDays(first, index));
  }, [calendarAnchor, calendarView]);
  const shellStyle = {
    '--brand-primary': brandPrimaryColor,
    '--brand-on-primary': getReadableBrandText(brandPrimaryColor),
  } as CSSProperties;

  const studentNav: Array<[StudentTab, string, IconName]> = [['home', '首頁', 'home'], ['booking', '預約', 'calendar'], ['courses', '課程', 'course'], ['teachers', '師資', 'teacher'], ['records', '課表', 'timetable']];
  const teacherNav: Array<[TeacherTab, string, IconName]> = [['today', '今日', 'teacher'], ['calendar', '行事曆', 'calendar'], ['sales', '售課', 'course'], ['history', '紀錄', 'history']];

  const isCalendarOverview = viewerMode === 'teacher' && teacherTab === 'calendar' && calendarRoute.mode === 'list';
  return <div className={`miniapp-shell${devPreviewEnabled ? ' has-dev-preview' : ''}${isCalendarOverview ? ` ${calendarShell.fullscreen}` : ''}`} style={shellStyle}>
    {devPreviewEnabled && <DevPreviewToolbar members={previewMembers} currentUserId={currentUserId} />}
    <div id="miniapp-overlay-root" className="miniapp-overlay-root" />
    <header className="app-header">
      <div className="brand-lockup">
        {storeIconUrl ? <img src={storeIconUrl} alt={`${storeName} 圖示`} /> : <div className="brand-monogram" aria-hidden="true">{createStoreMonogram(storeName)}</div>}
        <div><p>{viewerMode === 'teacher' ? 'TEACHER MODE' : 'COURSE BOOKING'}</p><h1>{storeName}</h1></div>
      </div>
      <div className="header-actions">
        <button type="button" className="member-avatar" aria-label="開啟會員選單" aria-expanded={profileOpen} onClick={() => setProfileOpen((open) => !open)}><Icon name={isTeacher ? 'teacher' : 'user'}/></button>
        {profileOpen && <div className="member-menu" role="dialog" aria-label="會員選單">
          <div className="member-menu-identity"><span className="member-menu-icon"><Icon name={isTeacher ? 'teacher' : 'user'}/></span><div><strong>{userName ?? (data.pendingMember ? '已使用 LINE 登入' : '尚未登入')}</strong><small>{devPreviewEnabled ? '開發預覽身分' : 'LINE 會員'}</small></div></div>
          {currentUserId && canClaimLegacyMember && <a className="member-mode-action" href="/member/complete"><Icon name="history"/><span>找回既有課程／請店家協助</span><Icon name="arrow"/></a>}
          {memberQrToken && <button type="button" className="member-mode-action" onClick={openMemberQr}><Icon name="course"/><span>開啟我的會員 QR Code</span><Icon name="arrow"/></button>}
          {isTeacher && <button type="button" className="member-mode-action" onClick={() => { navigateTo(viewerMode === 'student' ? '/teacher/today' : '/home'); setProfileOpen(false); }}><Icon name={viewerMode === 'student' ? 'teacher' : 'user'}/><span>切換為{viewerMode === 'student' ? '老師' : '學員'}模式</span><Icon name="arrow"/></button>}
          {devPreviewEnabled && <p>可從上方開發工具列切換測試會員。</p>}
        </div>}
      </div>
    </header>


    {!userName && !data.pendingMember && !devPreviewEnabled && <div className="login-panel"><LineLoginBootstrap liffId={liffId} /></div>}

    {memberQrOpen && memberQrToken && memberCode && <MemberQrCard token={memberQrToken} memberName={userName || 'LINE 會員'} memberCode={memberCode} onClose={closeMemberQr}/>}
    <main className={`app-content${isCalendarOverview ? ` ${calendarShell.content}` : ''}`} aria-busy={!routeDataReady}>
      <MiniAppPageTransition key={viewTransitionKey} ready={routeDataReady} firstReveal={firstReveal} animateReturn={animateReturn} skeleton={<MiniAppPageSkeleton path={routePath} isTeacher={isTeacher} userName={userName} homeTitle={homeTitle} homeDescription={homeDescription} homeEyebrow={homeEyebrow} />}>
      {hasData && <div>

      {viewerMode === 'student' && studentTab === 'home' && <>
        {currentUserId && canClaimLegacyMember && !credits.length && <MemberRecoveryNotice expiresAt={data.recoveryNoticeExpiresAt ?? null} />}
        <section className={`booking-hero ${memberCardStyles.card}`}>
          <div className={memberCardStyles.details}>
            <div className="booking-hero-copy">
              <span className="hero-kicker">{homeEyebrow || 'COURSE BOOKING'}</span>
              <h2>{resolvedHomeTitle}</h2>
              <p>{resolvedHomeDescription}</p>
            </div>
            <div className="credit-summary"><strong>{totalCredits}</strong><small>可用堂數</small></div>
          </div>
          {memberQrToken && memberCode && <button type="button" className={memberCardStyles.button} onClick={openMemberQr} aria-haspopup="dialog" aria-label="開啟會員 QR Code"><span className={memberCardStyles.memberNumber}><span className={memberCardStyles.numberLabel}><Icon name="number"/>會員編號</span><strong>{memberCode}</strong></span><span className={memberCardStyles.qrAction}><span className={memberCardStyles.qrTile}><Icon name="qr"/></span><span>開啟 QR Code</span></span></button>}
        </section>

        {nextBooking ? <section className="next-session-card"><div className="next-session-date"><span>NEXT</span><strong>{nextBookingDate?.date}</strong><small>{nextBookingDate?.weekday}</small></div><div className="next-session-info"><StatusPill status={nextBooking.status}/><h3>{nextBooking.courseName}</h3><p>{nextBooking.startsAtLabel}</p></div><button type="button" onClick={() => goStudent('records')} aria-label="查看預約"><Icon name="arrow"/></button></section> : <EmptyState title="目前沒有即將到來的課程" text={totalCredits > 0 ? '選擇適合的課程與時段完成預約。' : '購買或取得課程額度後，即可開始預約。'} action={<AppButton onClick={() => goStudent(totalCredits > 0 ? 'booking' : 'courses')}>{totalCredits > 0 ? '查看可預約時段' : '查看課程方案'}</AppButton>} />}

        <section className="metric-grid">
          <button type="button" onClick={() => goStudent('courses')}><span>可用額度</span><strong>{totalCredits}<small> 堂</small></strong><em>{reservedCredits ? `${reservedCredits} 堂已保留` : '查看效期與使用狀態'}</em></button>
          <button type="button" onClick={() => goStudent('booking')}><span>可預約時間</span><strong>查看時段</strong><em>選擇未來 30 天的課程</em></button>
        </section>

        <BookedTeacherStrip teachers={bookedTeachers} onSelect={openTeacher} onViewAll={() => goStudent('teachers')}/>
      </>}

      {viewerMode === 'student' && studentTab === 'booking' && <>
        {scopedBookingTeacherSlug && !scopedBookingTeacher
          ? <section className="teacher-missing"><div aria-hidden="true">?</div><span>TEACHER SCHEDULE</span><h2>找不到這位老師</h2><p>這位老師可能已停用，或不屬於目前的商店。</p><button type="button" onClick={() => navigateTo('/booking', { replace: true })}>查看全部可預約時段</button></section>
          : <StudentBookingBrowser timezone={props.timezone} routePath={routePath} slots={slots} groupSessions={groupSessions} courses={bookingCourses} venues={bookingVenues} dateOptions={bookingDates} bookings={bookings} credits={credits} isLoggedIn={Boolean(userName)} scopedTeacher={scopedBookingTeacher ? { id: scopedBookingTeacher.id, name: scopedBookingTeacher.name, slug: scopedBookingTeacher.slug, avatarUrl: scopedBookingTeacher.avatarUrl } : null} onNavigate={navigateTo}/>
        }
      </>}

      {viewerMode === 'student' && studentTab === 'courses' && <>
        <SectionTitle eyebrow="MY COURSES">我的課程</SectionTitle>
        <OwnedCoursePlans credits={credits} now={bookingClock} timezone={props.timezone ?? 'Asia/Taipei'}/>
        <SectionTitle eyebrow="PLANS">課程方案</SectionTitle>
        <CoursePlanBrowser plans={plans.filter(p => p.isPublic !== false)} storeName={storeName} storeIconUrl={storeIconUrl} selectedPlanId={routePath.match(/^\/courses\/plans\/([^/]+)$/)?.[1] ?? null} onSelectPlan={openCoursePlan} onClosePlan={closeCoursePlan}/>
      </>}

      {viewerMode === 'student' && studentTab === 'teachers' && (
        <TeacherBrowser teachers={teachers} selectedSlug={selectedTeacherSlug} onSelect={openTeacher} onBack={closeTeacher} onBook={bookTeacher}/>
      )}

      {viewerMode === 'student' && studentTab === 'records' && <StudentRecords bookings={bookings} now={bookingClock} timezone={props.timezone ?? 'Asia/Taipei'} renderActions={booking => <BookingActions booking={booking} />} />}

      {viewerMode === 'teacher' && routePath === '/teacher/booking' && <TeacherBooking onBack={() => navigateTo('/teacher/today')} />}
      {viewerMode === 'teacher' && teacherTab === 'today' && routePath !== '/teacher/booking' && <TeacherToday bookings={teacherBookings} timezone={props.timezone ?? 'Asia/Taipei'} onBook={() => navigateTo('/teacher/booking')} onCalendar={() => goTeacher('calendar')} onCompensation={() => goTeacher('compensation')} />}

      {viewerMode === 'teacher' && teacherTab === 'calendar' && <TeacherCalendar bookings={teacherBookings} route={calendarRoute} calendarView={calendarView} setCalendarView={setCalendarView} calendarAnchor={calendarAnchor} setCalendarAnchor={setCalendarAnchor} calendarDays={calendarDays} occurrences={teacherOccurrences} venues={teacherVenues} rooms={teacherRooms} courses={teacherCourses} onCreate={openAvailabilityCreate} onOpen={openAvailabilityDetail} onEdit={openAvailabilityEdit} onBack={closeAvailabilityView} createAction={createAvailability} updateAction={updateAvailability} closeAction={closeAvailability}/>}

      {viewerMode === 'teacher' && teacherTab === 'compensation' && <TeacherCompensation timezone={props.timezone ?? 'Asia/Taipei'} onBack={() => goTeacher('today')} />}
      {viewerMode === 'teacher' && teacherTab === 'sales' && <>
        <TeacherCourseSale plans={plans} initialSales={teacherSales} activeDialog={saleDialog} saleView={saleView} isCreating={isSaleCreating} onCreate={createSale} onOpenDialog={openSaleDialog} onCloseDialog={closeSaleDialog} onOpenSale={openSaleDetail} onSaleCompleted={showSaleComplete} onCloseSaleView={closeSaleView}/>
      </>}

      {viewerMode === 'teacher' && teacherTab === 'history' && <><SectionTitle eyebrow="TEACHING HISTORY">授課紀錄</SectionTitle><div className="card-stack">{teacherHistory.map((record) => <article className="record-card" key={record.id}><div><h3>{record.courseName}</h3><p>{record.startsAtLabel} · {formatDurationMinutes(record.durationMinutes)}</p><small>出席 {record.attendedCount}/{record.participantCount} 人</small></div><StatusPill status={record.outcome}/></article>)}</div>{!teacherHistory.length && <EmptyState title="尚無授課紀錄" text="完成核銷後，授課事實會保存於這裡。"/>}</>}
    </div>}</MiniAppPageTransition></main>

    <nav className="app-tabbar" aria-label={viewerMode === 'student' ? '學員導覽' : '老師導覽'}>{viewerMode === 'student' ? studentNav.map(([tab, label, icon]) => <button key={tab} type="button" className={studentTab === tab ? 'active' : ''} onClick={() => goStudent(tab)}><Icon name={icon}/><span>{label}</span></button>) : teacherNav.map(([tab, label, icon]) => <button key={tab} type="button" className={teacherTab === tab ? 'active' : ''} onClick={() => goTeacher(tab)}><Icon name={icon}/><span>{label}</span></button>)}</nav>
  </div>;
}

function BookingActions({ booking }: { booking: Booking }) {
  return <>{booking.isPrimary && booking.status === 'BOOKED' && <div className="secondary-actions"><StudentCancellationButton bookingId={booking.id}/>{!booking.guestCount && !booking.groupStatus && <BookingInvitationButton bookingId={booking.id}/>}{booking.participants.filter((p) => p.role === 'COMPANION' && p.status === 'ACCEPTED').map((participant) => <form key={participant.userId} action={removeMiniAppBookingParticipantAction}><input type="hidden" name="bookingId" value={booking.id}/><input type="hidden" name="participantUserId" value={participant.userId}/><button type="submit">移除 {participant.name || '同行者'}</button></form>)}</div>}</>;
}

function TeacherCalendar({ bookings, route, calendarView, setCalendarView, calendarAnchor, setCalendarAnchor, calendarDays, occurrences, venues, rooms, courses, onCreate, onOpen, onEdit, onBack, createAction, updateAction, closeAction }: {
  bookings: Booking[];
  route: CalendarRoute;
  calendarView: 'week' | 'month';
  setCalendarView: (value: 'week' | 'month') => void;
  calendarAnchor: Date;
  setCalendarAnchor: (value: Date) => void;
  calendarDays: Date[];
  occurrences: TeacherOccurrence[];
  venues: SimpleOption[];
  rooms: RoomOption[];
  courses: SimpleOption[];
  onCreate: () => void;
  onOpen: (occurrenceId: string) => void;
  onEdit: (occurrenceId: string) => void;
  onBack: () => void;
  createAction: (formData: FormData) => Promise<void>;
  updateAction: (formData: FormData) => Promise<void>;
  closeAction: (formData: FormData) => Promise<void>;
}) {
  const selected = route.occurrenceId ? occurrences.find((item) => item.id === route.occurrenceId) ?? null : null;

  if (route.mode === 'new') {
    return <section className="availability-task-page">
      <AvailabilityPageHeading eyebrow="NEW AVAILABILITY" title="新增開放時間" description="設定時間、候選教室與可預約課程。" onBack={onBack}/>
      <AvailabilityCreateForm day={calendarAnchor} venues={venues} rooms={rooms} courses={courses} action={createAction}/>
    </section>;
  }

  if ((route.mode === 'detail' || route.mode === 'edit') && !selected) {
    return <section className="availability-task-page">
      <AvailabilityPageHeading eyebrow="AVAILABILITY" title="找不到開放設定" description="這筆設定可能已被移除，或不屬於目前老師。" onBack={onBack}/>
      <EmptyState title="無法顯示這筆設定" text="返回行事曆查看目前仍可管理的開放時間。" action={<AppButton onClick={onBack}>返回行事曆</AppButton>}/>
    </section>;
  }

  if (selected && route.mode === 'edit') {
    return <section className="availability-task-page">
      <AvailabilityPageHeading eyebrow="EDIT AVAILABILITY" title="編輯開放時間" description="選擇修改本次、後續或整組設定。" onBack={() => onOpen(selected.id)}/>
      <section className="availability-editor availability-editor-page">
        <OccurrenceEditor item={selected} venues={venues} rooms={rooms} courses={courses} action={updateAction}/>
      </section>
    </section>;
  }

  if (selected && route.mode === 'detail') {
    const startsAt = new Date(selected.startsAt);
    const endsAt = new Date(selected.endsAt);
    const durationMinutes = Math.round((endsAt.getTime() - startsAt.getTime()) / 60000);
    const selectedLocations = selected.locations.map((location) => {
      const venueName = venues.find((venue) => venue.id === location.venueId)?.name ?? '未知場館';
      const roomName = rooms.find((room) => room.id === location.roomId)?.name ?? '未知教室';
      return `${venueName}／${roomName}`;
    });
    const selectedCourses = selected.courseIds.map((courseId) => courses.find((course) => course.id === courseId)?.name ?? '未知課程');
    const openSeriesOccurrences = occurrences
      .filter((item) => item.status === 'OPEN' && (selected.seriesId ? item.seriesId === selected.seriesId : item.id === selected.id))
      .sort((left, right) => new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime());
    const followingOccurrences = openSeriesOccurrences.filter((item) => new Date(item.startsAt).getTime() >= startsAt.getTime());
    const closeOptions: Array<{ scope: AvailabilityCloseScope; label: string; description: string; occurrences: TeacherOccurrence[] }> = [
      { scope: 'SINGLE', label: '只關閉這一次', description: '只影響目前查看的開放時間。', occurrences: [selected] },
      ...(followingOccurrences.length > 1 ? [{ scope: 'FOLLOWING' as const, label: '關閉本次及後續', description: `保留較早的時段，關閉目前起共 ${followingOccurrences.length} 筆。`, occurrences: followingOccurrences }] : []),
      ...(openSeriesOccurrences.length > 1 ? [{ scope: 'SERIES' as const, label: '關閉整組設定', description: `關閉這組仍開放的 ${openSeriesOccurrences.length} 筆時段。`, occurrences: openSeriesOccurrences }] : []),
    ];
    return <section className="availability-task-page">
      <AvailabilityPageHeading eyebrow="AVAILABILITY DETAIL" title="開放時間詳情" description="確認這個時段允許預約的地點與課程。" onBack={onBack} action={selected.status === 'OPEN' ? <AppButton onClick={() => onEdit(selected.id)}>編輯</AppButton> : undefined}/>
      <article className="availability-detail-card">
        <header><div className="availability-detail-date"><span>{startsAt.toLocaleDateString('zh-TW', { month: 'short' })}</span><strong>{startsAt.getDate()}</strong><small>{startsAt.toLocaleDateString('zh-TW', { weekday: 'short' })}</small></div><div><StatusPill status={selected.status}/><h3>{startsAt.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })} — {endsAt.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</h3><p>{startsAt.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric' })} · {formatDurationMinutes(durationMinutes)}</p></div></header>
        <AvailabilityDetailGroup icon="location" title="候選場館與教室" items={selectedLocations}/>
        <AvailabilityDetailGroup icon="course" title="可預約課程" items={selectedCourses}/>
        <div className="availability-detail-policy"><span>最晚預約時間</span><strong>課前 {formatDurationMinutes(selected.minBookingNoticeMinutes)}</strong></div>
      </article>
      {selected.status === 'OPEN' && <AvailabilityCloseControl occurrence={selected} options={closeOptions} action={closeAction}/>}
    </section>;
  }

  return <TeacherCalendarTimeline bookings={bookings} view={calendarView} onViewChange={setCalendarView} day={calendarAnchor} onDayChange={setCalendarAnchor} days={calendarDays} occurrences={occurrences} venues={venues} rooms={rooms} courses={courses} onCreate={onCreate} onOpen={onOpen}/>;
}

function AvailabilityPageHeading({ eyebrow, title, description, onBack, action }: { eyebrow: string; title: string; description: string; onBack: () => void; action?: ReactNode }) {
  return <header className="availability-page-heading"><button type="button" onClick={onBack} aria-label="返回上一頁">‹</button><div><span>{eyebrow}</span><h2>{title}</h2><p>{description}</p></div>{action && <div>{action}</div>}</header>;
}

function AvailabilityDetailGroup({ icon, title, items }: { icon: IconName; title: string; items: string[] }) {
  return <section className="availability-detail-group"><header><Icon name={icon}/><strong>{title}</strong><span>{items.length}</span></header><div>{items.map((item) => <p key={item}>{item}</p>)}</div></section>;
}

function AvailabilityLocationPicker({ venues, rooms, selectedScopes, onChange }: {
  venues: SimpleOption[];
  rooms: RoomOption[];
  selectedScopes: Set<string>;
  onChange: (value: Set<string>) => void;
}) {
  const allRoomScopes = rooms.map((room) => `${room.venueId}:${room.id}`);
  const areAllRoomsSelected = allRoomScopes.length > 0 && allRoomScopes.every((scope) => selectedScopes.has(scope));

  function toggleScopes(scopes: string[], checked: boolean) {
    const next = new Set(selectedScopes);
    scopes.forEach((scope) => checked ? next.add(scope) : next.delete(scope));
    onChange(next);
  }

  return <fieldset className="availability-location-picker"><legend>開放場館與教室</legend>
    <div className="availability-location-toolbar"><span>{selectedScopes.size ? `已選 ${selectedScopes.size} 個候選教室` : '可複選'}</span>{allRoomScopes.length > 0 && <label><input type="checkbox" checked={areAllRoomsSelected} onChange={(event) => toggleScopes(allRoomScopes, event.target.checked)}/><span>全選所有教室</span></label>}</div>
    {venues.map((venue) => {
      const venueRooms = rooms.filter((room) => room.venueId === venue.id);
      const venueScopes = venueRooms.map((room) => `${venue.id}:${room.id}`);
      const areVenueRoomsSelected = venueScopes.length > 0 && venueScopes.every((scope) => selectedScopes.has(scope));
      return <section key={venue.id} className="availability-location-group">
        <header><strong>{venue.name}</strong>{venueRooms.length > 0 && <label><input type="checkbox" checked={areVenueRoomsSelected} onChange={(event) => toggleScopes(venueScopes, event.target.checked)}/><span>全選此場館</span></label>}</header>
        {venueRooms.map((room) => {
          const roomKey = `${venue.id}:${room.id}`;
          return <label key={room.id}><input type="checkbox" name="availabilityScopes" value={roomKey} checked={selectedScopes.has(roomKey)} onChange={(event) => toggleScopes([roomKey], event.target.checked)}/><span>{room.name}</span></label>;
        })}
        {!venueRooms.length && <p className="availability-form-note warning">此場館尚未設定可用教室。</p>}
      </section>;
    })}
  </fieldset>;
}

function AvailabilityCreateForm({ day, venues, rooms, courses, action }: { day: Date; venues: SimpleOption[]; rooms: RoomOption[]; courses: SimpleOption[]; action: (formData: FormData) => Promise<void> }) {
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(() => new Set());
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(() => new Set());
  const areAllCoursesSelected = courses.length > 0 && courses.every((course) => selectedCourseIds.has(course.id));
  const isReady = selectedScopes.size > 0 && selectedCourseIds.size > 0;

  function toggleAllCourses(checked: boolean) {
    setSelectedCourseIds(new Set(checked ? courses.map((course) => course.id) : []));
  }

  function toggleCourse(courseId: string, checked: boolean) {
    setSelectedCourseIds((current) => {
      const next = new Set(current);
      if (checked) next.add(courseId);
      else next.delete(courseId);
      return next;
    });
  }

  return <section className="availability-editor availability-editor-page">
    <form action={action}>
      <div className="form-grid">
        <AvailabilityTimeFields initialStart={localDateTime(new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9).toISOString())} initialEnd={localDateTime(new Date(day.getFullYear(), day.getMonth(), day.getDate(), 10).toISOString())}/>
      </div>
      <AvailabilityLocationPicker venues={venues} rooms={rooms} selectedScopes={selectedScopes} onChange={setSelectedScopes}/>
      {!venues.length && <p className="availability-form-note warning" role="status">尚未被指派可服務場館，請由 Seller 後台在老師設定中加入場館。</p>}
      <fieldset className="availability-course-picker"><legend>可教授課程</legend>
        <div className="availability-course-toolbar"><span>{selectedCourseIds.size ? `已選 ${selectedCourseIds.size} 門課程` : '可複選'}</span>{courses.length > 0 && <label><input type="checkbox" checked={areAllCoursesSelected} onChange={(event) => toggleAllCourses(event.target.checked)}/><span>全選所有課程</span></label>}</div>
        <section className="availability-course-group">{courses.map((course) => <label key={course.id}><input type="checkbox" name="courseIds" value={course.id} checked={selectedCourseIds.has(course.id)} onChange={(event) => toggleCourse(course.id, event.target.checked)}/><span>{course.name}</span></label>)}{!courses.length && <p className="availability-form-note warning" role="status">尚未設定可教授課程，請先由 Seller 後台完成老師的課程指派。</p>}</section>
      </fieldset>
      <div className="form-grid"><label>重複<select name="frequency" defaultValue="NONE"><option value="NONE">不重複</option><option value="DAILY">每日／每 N 日</option><option value="WEEKLY">每週</option></select></label><label>重複次數<input name="occurrenceCount" type="number" defaultValue="1" min="1"/></label><label>間隔<input name="interval" type="number" defaultValue="1" min="1"/></label><label>最晚預約分鐘<input name="minBookingNoticeMinutes" type="number" defaultValue="120" min="0"/></label></div>
      <input type="hidden" name="until" value=""/>
      <AppButton type="submit" disabled={!isReady}>建立開放區間</AppButton>
    </form>
  </section>;
}

function OccurrenceEditor({ item, venues, rooms, courses, action }: { item: TeacherOccurrence; venues: SimpleOption[]; rooms: RoomOption[]; courses: SimpleOption[]; action: (formData: FormData) => Promise<void> }) {
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(() => new Set(
    item.locations.map((location) => `${location.venueId}:${location.roomId}`)
  ));
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<string>>(() => new Set(item.courseIds));
  const areAllCoursesSelected = courses.length > 0 && courses.every((course) => selectedCourseIds.has(course.id));

  function toggleAllCourses(checked: boolean) {
    setSelectedCourseIds(new Set(checked ? courses.map((course) => course.id) : []));
  }

  function toggleCourse(courseId: string, checked: boolean) {
    const next = new Set(selectedCourseIds);
    if (checked) next.add(courseId);
    else next.delete(courseId);
    setSelectedCourseIds(next);
  }

  return <>
    <form action={action}>
      <input type="hidden" name="occurrenceId" value={item.id}/>
      <div className="form-grid">
        <AvailabilityTimeFields initialStart={localDateTime(item.startsAt)} initialEnd={localDateTime(item.endsAt)}/>
        <label>調整範圍<select name="scope"><option value="SINGLE">僅本次</option><option value="FOLLOWING">本次及後續</option><option value="SERIES">整組</option></select></label>
        <label>最晚預約分鐘<input name="minBookingNoticeMinutes" type="number" defaultValue={item.minBookingNoticeMinutes}/></label>
      </div>
      <AvailabilityLocationPicker venues={venues} rooms={rooms} selectedScopes={selectedScopes} onChange={setSelectedScopes}/>
      <fieldset className="availability-course-picker"><legend>可教授課程</legend>
        <div className="availability-course-toolbar"><span>{selectedCourseIds.size ? `已選 ${selectedCourseIds.size} 門課程` : '可複選'}</span>{courses.length > 0 && <label><input type="checkbox" checked={areAllCoursesSelected} onChange={(event) => toggleAllCourses(event.target.checked)}/><span>全選所有課程</span></label>}</div>
        <section className="availability-course-group">{courses.map((course) => <label key={course.id}><input type="checkbox" name="courseIds" value={course.id} checked={selectedCourseIds.has(course.id)} onChange={(event) => toggleCourse(course.id, event.target.checked)}/><span>{course.name}</span></label>)}</section>
      </fieldset>
      <AppButton type="submit" disabled={!selectedScopes.size || !selectedCourseIds.size}>儲存修改</AppButton>
    </form>
  </>;
}

function AvailabilityCloseControl({ occurrence, options, action }: {
  occurrence: TeacherOccurrence;
  options: Array<{ scope: AvailabilityCloseScope; label: string; description: string; occurrences: TeacherOccurrence[] }>;
  action: (formData: FormData) => Promise<void>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [scope, setScope] = useState<AvailabilityCloseScope>('SINGLE');
  const selectedOption = options.find((option) => option.scope === scope) ?? options[0]!;

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  function openDialog() {
    setScope('SINGLE');
    setIsOpen(true);
  }

  return <>
    <button className="availability-close-trigger" type="button" onClick={openDialog}>關閉開放時間</button>
    {isOpen && <AppOverlayPortal>
      <div className="availability-close-backdrop" role="presentation" onClick={(event) => {
        if (event.target === event.currentTarget) setIsOpen(false);
      }}>
        <section className="availability-close-dialog" role="dialog" aria-modal="true" aria-labelledby="availability-close-title" aria-describedby="availability-close-description">
          <header>
            <span aria-hidden="true">!</span>
            <div><small>CLOSE AVAILABILITY</small><h2 id="availability-close-title">確認關閉開放時間</h2></div>
            <button type="button" aria-label="關閉確認視窗" onClick={() => setIsOpen(false)}>×</button>
          </header>
          <p id="availability-close-description">關閉後，學員將無法再預約所選範圍內的時段。已有預約的時段不會被直接關閉。</p>
          {options.length === 1 ? <div className="availability-close-single-scope"><strong>只關閉這一次</strong><span>這不是重複開放設定，不會影響其他時段。</span></div> : <fieldset className="availability-close-scopes"><legend>選擇關閉範圍</legend>{options.map((option) => <label key={option.scope} className={scope === option.scope ? 'selected' : ''}><input type="radio" name="closeScopePreview" value={option.scope} checked={scope === option.scope} onChange={() => setScope(option.scope)}/><span><strong>{option.label}</strong><small>{option.description}</small></span><b>{option.occurrences.length} 筆</b></label>)}</fieldset>}
          <section className="availability-close-preview">
            <header><div><small>PREVIEW</small><h3>將關閉 {selectedOption.occurrences.length} 筆開放時間</h3></div><b>{selectedOption.occurrences.length}</b></header>
            <div>{selectedOption.occurrences.slice(0, 4).map((item) => {
              const start = new Date(item.startsAt);
              const end = new Date(item.endsAt);
              return <p key={item.id}><span>{start.toLocaleDateString('zh-TW', { month: 'long', day: 'numeric', weekday: 'short' })}</span><strong>{start.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })} — {end.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}</strong></p>;
            })}</div>
            {selectedOption.occurrences.length > 4 && <small className="availability-close-more">另有 {selectedOption.occurrences.length - 4} 筆未列出</small>}
          </section>
          <form className="availability-close-actions" action={action}>
            <input type="hidden" name="occurrenceId" value={occurrence.id}/>
            <input type="hidden" name="scope" value={selectedOption.scope}/>
            <button type="button" onClick={() => setIsOpen(false)}>返回</button>
            <button type="submit">確認關閉 {selectedOption.occurrences.length} 筆</button>
          </form>
        </section>
      </div>
    </AppOverlayPortal>}
  </>;
}

function AvailabilityTimeFields({ initialStart = '', initialEnd = '' }: { initialStart?: string; initialEnd?: string }) {
  const [startsAt, setStartsAt] = useState(() => snapToHalfHour(initialStart));
  const [endsAt, setEndsAt] = useState(() => snapToHalfHour(initialEnd));
  const minimumEnd = startsAt ? addLocalMinutes(startsAt, 60) : '';

  function changeStart(value: string) {
    const normalized = snapToHalfHour(value);
    setStartsAt(normalized);
    if (!normalized) return;
    const nextMinimumEnd = addLocalMinutes(normalized, 60);
    if (!endsAt || endsAt < nextMinimumEnd) setEndsAt(nextMinimumEnd);
  }

  function changeEnd(value: string) {
    const normalized = snapToHalfHour(value);
    setEndsAt(minimumEnd && normalized < minimumEnd ? minimumEnd : normalized);
  }

  return <>
    <label>開始<input name="startsAt" type="datetime-local" step="1800" value={startsAt} onInput={(event) => changeStart(event.currentTarget.value)} required/></label>
    <label>結束<input name="endsAt" type="datetime-local" step="1800" min={minimumEnd} value={endsAt} onInput={(event) => changeEnd(event.currentTarget.value)} required/></label>
    <p className="availability-time-hint">時間以 30 分鐘為單位，開放區間至少 1 小時。</p>
  </>;
}

function snapToHalfHour(value: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  date.setSeconds(0, 0);
  const minutes = date.getMinutes();
  if (minutes !== 0 && minutes !== 30) date.setMinutes(minutes < 30 ? 30 : 60, 0, 0);
  return localDateTime(date.toISOString());
}

function addLocalMinutes(value: string, minutes: number) {
  const date = new Date(value);
  date.setMinutes(date.getMinutes() + minutes);
  return localDateTime(date.toISOString());
}
